import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import MoneyInput from '../shared/MoneyInput';
import NumberInput from '../shared/NumberInput';
import StateSelect from '../shared/StateSelect';
import ContactPicker from '../shared/ContactPicker';
import {
  qualificationApi, EMPTY_INPUT,
  VETERAN_LABELS, PROPERTY_TYPE_LABELS, PROPERTY_USE_LABELS,
  CREDIT_RANGE_LABELS, BANKRUPTCY_LABELS, FORECLOSURE_LABELS,
  INCOME_TYPE_LABELS, CALC_INCOME_TYPE_LABELS, EMPLOYMENT_LABELS, DOWN_SOURCE_LABELS,
} from '../qualificationApi';
import { refusalText } from '../income-calculator/constants';
import QualificationResults from './QualificationResults';
import WizardTaxLookupDialog from './WizardTaxLookupDialog';
import { formatDate } from '../kit/time';

const STEPS = ['Borrower', 'Property', 'Credit', 'Income & Assets', 'Review'];
const TERM_OPTIONS = [
  { v: 360, l: '30 years (360 mo)' },
  { v: 240, l: '20 years (240 mo)' },
  { v: 180, l: '15 years (180 mo)' },
];

function LabeledSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// `scenario` = a saved scenario being edited: the wizard opens on its inputs,
// "Save changes" updates it (onUpdated) and "Save as new scenario" copies it (onSaved).
export default function QualificationWizard({ contactId, loanId, standalone = false, initialInput, scenario, onSaved, onUpdated, onCancel }) {
  const editing = scenario || null;
  const startInput = editing ? editing.input : initialInput;
  const [step, setStep] = useState(1);
  const [input, setInput] = useState(() => ({ ...EMPTY_INPUT, ...(startInput || {}) }));
  const [incomeCalcs, setIncomeCalcs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [preview, setPreview] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [name, setName] = useState(editing ? editing.name : `Scenario ${new Date().toLocaleDateString()}`);
  const [saving, setSaving] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [taxLabel, setTaxLabel] = useState('');
  // Standalone (Calculators > Affordability): the contact is picked at save time.
  const [pickedContact, setPickedContact] = useState(null);
  const [savedTo, setSavedTo] = useState(null); // {contact_id, scenario_id} after a standalone save
  const saveContactId = contactId || pickedContact;
  const reqId = useRef(0);
  const timer = useRef(null);

  const set = (patch) => { setSavedTo(null); setInput((prev) => ({ ...prev, ...patch })); };

  // Prefill assumptions from tenant defaults unless the caller already gave us input.
  useEffect(() => {
    if (startInput) return;
    qualificationApi.getDefaults().then((d) => {
      set({
        property_tax_rate: d.property_tax_rate,
        insurance_rate: d.insurance_rate,
        hoa_monthly: d.hoa_monthly,
        term_months: d.term_months,
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!contactId) return;
    qualificationApi.listIncomeCalcs(contactId)
      .then((d) => setIncomeCalcs(d.income_calcs || []))
      .catch(() => setIncomeCalcs([]));
  }, [contactId]);

  useEffect(() => {
    qualificationApi.listPrograms()
      .then((d) => setPrograms((d.programs || []).filter((p) => p.active)))
      .catch(() => setPrograms([]));
  }, []);

  // Live preview: debounced 400ms recalculation from step 2 onward.
  useEffect(() => {
    if (step < 2) return;
    if (timer.current) clearTimeout(timer.current);
    const myReq = ++reqId.current;
    setCalculating(true);
    timer.current = setTimeout(() => {
      qualificationApi.calculate(input)
        .then((res) => { if (reqId.current === myReq) { setPreview(res); setCalculating(false); } })
        .catch(() => { if (reqId.current === myReq) setCalculating(false); });
    }, 400);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, step]);

  const pickIncomeCalc = (calc) => {
    set({
      income_source: 'calculated',
      income_calc_id: calc.calc_id,
      program_monthly_income: calc.program_monthly_income,
    });
  };
  const pickManualIncome = () => {
    set({ income_source: 'manual', income_calc_id: null, program_monthly_income: null });
  };

  const rateOverride = (code) => input.rate_overrides?.[code] ?? '';
  const setRateOverride = (code, val) => {
    const next = { ...(input.rate_overrides || {}) };
    if (val === null || val === '') delete next[code];
    else next[code] = val;
    set({ rate_overrides: next });
  };

  const saveScenario = async ({ asNew = false } = {}) => {
    if (!saveContactId) return;
    setSaving(true);
    try {
      if (editing && !asNew) {
        const updated = await qualificationApi.updateScenario(editing.scenario_id, { name, input });
        toast({ title: 'Scenario updated' });
        onUpdated?.(updated);
      } else {
        const body = { name, input, ...(loanId ? { loan_id: loanId } : {}) };
        const created = await qualificationApi.createScenario(saveContactId, body);
        toast({ title: 'Scenario saved' });
        if (standalone) setSavedTo({ contact_id: saveContactId, scenario_id: created?.scenario_id });
        onSaved?.(created);
      }
    } catch (e) {
      toast({ title: 'Could not save scenario', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const generateProgramSet = async () => {
    if (!contactId) return;
    setSaving(true);
    try {
      const body = { input, name_prefix: name, ...(loanId ? { loan_id: loanId } : {}) };
      const { scenarios } = await qualificationApi.createProgramSet(contactId, body);
      toast({ title: `Generated ${scenarios.length} scenario${scenarios.length === 1 ? '' : 's'}` });
      onSaved?.(scenarios[0]);
    } catch (e) {
      toast({ title: 'Could not generate program set', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  // Ticket 131: rate looked up at the entered purchase price. Editing the rate
  // by hand afterwards flips the source back to manual (step 4).
  const applyTaxLookup = ({ rate, label }) => {
    set({ property_tax_rate: rate, property_tax_source: 'lookup' });
    setTaxLabel(label);
  };
  const taxLookedUp = input.property_tax_source === 'lookup';
  // A looked-up rate is a Texas rate; outside Texas it is just a typed number.
  const changeState = (v) => {
    if (v !== 'TX' && taxLookedUp) {
      set({ property_state: v, property_tax_source: 'manual' });
      setTaxLabel('');
    } else {
      set({ property_state: v });
    }
  };

  const next = () => setStep((s) => Math.min(5, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {editing && <div className="text-xs text-slate-500" data-testid="wizard-editing">Editing {editing.name}</div>}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && <span className="text-slate-300">/</span>}
              <span className={step === i + 1 ? 'font-semibold' : ''} style={step === i + 1 ? { color: '#070B5F' } : undefined}>{s}</span>
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={`wizard-step-${step}`}>
          {step === 1 && (
            <div className="space-y-4">
              <LabeledSelect label="Borrower Type" value={input.borrower_type}
                onChange={(v) => set({ borrower_type: v })}
                options={{ individual: 'Individual', joint: 'Joint' }} testid="wizard-borrower-type" />
              <div className="flex items-center gap-2">
                <Checkbox checked={input.first_time_buyer} onCheckedChange={(v) => set({ first_time_buyer: !!v })} data-testid="wizard-first-time-buyer" />
                <Label className="text-sm">First-time buyer</Label>
              </div>
              <LabeledSelect label="Veteran Status" value={input.veteran_status}
                onChange={(v) => set({ veteran_status: v })} options={VETERAN_LABELS} testid="wizard-veteran-status" />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <LabeledSelect label="Property Type" value={input.property_type}
                onChange={(v) => set({ property_type: v })} options={PROPERTY_TYPE_LABELS} testid="wizard-property-type" />
              <LabeledSelect label="Property Use" value={input.property_use}
                onChange={(v) => set({ property_use: v })} options={PROPERTY_USE_LABELS} testid="wizard-property-use" />
              <StateSelect label="Property State" value={input.property_state} onChange={changeState} testId="wizard-property-state" />
              <div>
                <Label className="text-xs text-slate-500">Purchase Price</Label>
                <MoneyInput value={input.purchase_price || null} onChange={(n) => set({ purchase_price: n ?? 0 })} className="mt-1" data-testid="wizard-purchase-price" />
              </div>
              {input.property_state === 'TX' && (
                <div className="text-sm">
                  <button type="button" onClick={() => setTaxOpen(true)} disabled={!(input.purchase_price > 0)}
                    className="underline disabled:no-underline disabled:text-slate-400" style={input.purchase_price > 0 ? { color: '#070B5F' } : undefined}
                    data-testid="wizard-tax-lookup-link">
                    Look up taxes
                  </button>
                  {!(input.purchase_price > 0) && <span className="ml-2 text-xs text-slate-400">Enter a purchase price first.</span>}
                  {taxLookedUp && taxLabel && (
                    <div className="mt-1 text-xs text-slate-500" data-testid="wizard-tax-applied">Property tax rate: {taxLabel}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Credit Score (optional)</Label>
                <NumberInput integer value={input.credit_score} min={300} max={850}
                  onChange={(n) => set({ credit_score: n, credit_score_range: n != null ? null : input.credit_score_range })}
                  className="mt-1" data-testid="wizard-credit-score" />
              </div>
              <LabeledSelect label="Or Credit Score Range" value={input.credit_score_range || ''}
                onChange={(v) => set({ credit_score_range: v, credit_score: null })}
                options={CREDIT_RANGE_LABELS} testid="wizard-credit-range" />
              <LabeledSelect label="Bankruptcy History" value={input.bankruptcy_history}
                onChange={(v) => set({ bankruptcy_history: v })} options={BANKRUPTCY_LABELS} testid="wizard-bankruptcy" />
              <LabeledSelect label="Foreclosure History" value={input.foreclosure_history}
                onChange={(v) => set({ foreclosure_history: v })} options={FORECLOSURE_LABELS} testid="wizard-foreclosure" />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              {contactId && (
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Income</Label>
                  <div className="space-y-2">
                    <button type="button" onClick={pickManualIncome}
                      data-testid="wizard-income-manual"
                      className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${input.income_source === 'manual' ? 'border-[#070B5F] bg-slate-50' : 'border-slate-200'}`}>
                      Enter manually
                    </button>
                    {incomeCalcs.map((c) => c.incomplete ? (
                      <button type="button" key={c.calc_id} disabled
                        data-testid={`wizard-income-calc-${c.calc_id}`}
                        className="w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center justify-between border-amber-200 bg-amber-50/50 opacity-80 cursor-not-allowed">
                        <span>{c.borrower_name || 'Borrower'} · {CALC_INCOME_TYPE_LABELS[c.income_type] || c.income_type}</span>
                        <span className="text-amber-700 text-xs">
                          {refusalText(c, 'Incomplete')} Fix in Income Calculator.
                        </span>
                      </button>
                    ) : (
                      <button type="button" key={c.calc_id} onClick={() => pickIncomeCalc(c)}
                        data-testid={`wizard-income-calc-${c.calc_id}`}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center justify-between ${input.income_calc_id === c.calc_id ? 'border-[#070B5F] bg-slate-50' : 'border-slate-200'}`}>
                        <span>{c.borrower_name || 'Borrower'} · {CALC_INCOME_TYPE_LABELS[c.income_type] || c.income_type}</span>
                        <span className="text-slate-500">${Number(c.qualifying_monthly || 0).toLocaleString()}/mo · {c.created_at ? formatDate(c.created_at) : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {input.income_source === 'manual' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Annual Income</Label>
                    <MoneyInput value={input.annual_income || null} onChange={(n) => set({ annual_income: n ?? 0 })} className="mt-1" data-testid="wizard-annual-income" />
                  </div>
                  <LabeledSelect label="Income Type" value={input.income_type} onChange={(v) => set({ income_type: v })} options={INCOME_TYPE_LABELS} testid="wizard-income-type" />
                  <LabeledSelect label="Employment Length" value={input.employment_length} onChange={(v) => set({ employment_length: v })} options={EMPLOYMENT_LABELS} testid="wizard-employment-length" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                <div>
                  <Label className="text-xs text-slate-500">Down Payment</Label>
                  <MoneyInput value={input.down_payment_amount || null} onChange={(n) => set({ down_payment_amount: n ?? 0 })} className="mt-1" data-testid="wizard-down-payment" />
                </div>
                <LabeledSelect label="Down Payment Source" value={input.down_payment_source} onChange={(v) => set({ down_payment_source: v })} options={DOWN_SOURCE_LABELS} testid="wizard-down-source" />
                <div>
                  <Label className="text-xs text-slate-500">Reserves (months)</Label>
                  <NumberInput integer value={input.reserves_months} onChange={(n) => set({ reserves_months: n ?? 0 })} className="mt-1" data-testid="wizard-reserves" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Monthly Debt Payments</Label>
                  <MoneyInput value={input.monthly_debt_payments || null} onChange={(n) => set({ monthly_debt_payments: n ?? 0 })} className="mt-1" data-testid="wizard-monthly-debts" />
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 space-y-3">
                <div className="text-xs uppercase tracking-widest text-slate-400">Assumptions</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Property Tax Rate</Label>
                    <NumberInput value={input.property_tax_rate} suffix="%" onChange={(n) => set({ property_tax_rate: n ?? 0, property_tax_source: 'manual' })} className="mt-1" data-testid="wizard-tax-rate" />
                    {taxLookedUp && <div className="mt-1 text-[11px] text-slate-500" data-testid="wizard-tax-source">{taxLabel || 'Looked up'}</div>}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Insurance Rate</Label>
                    <NumberInput value={input.insurance_rate} suffix="%" onChange={(n) => set({ insurance_rate: n ?? 0 })} className="mt-1" data-testid="wizard-insurance-rate" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">HOA (monthly)</Label>
                    <MoneyInput value={input.hoa_monthly || null} onChange={(n) => set({ hoa_monthly: n ?? 0 })} className="mt-1" data-testid="wizard-hoa" />
                  </div>
                  <LabeledSelect label="Term" value={String(input.term_months)}
                    onChange={(v) => set({ term_months: Number(v) })}
                    options={Object.fromEntries(TERM_OPTIONS.map((t) => [String(t.v), t.l]))} testid="wizard-term" />
                </div>
                {programs.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400 mb-1 mt-2">Rate overrides (optional)</div>
                    <div className="space-y-2">
                      {programs.map((p) => (
                        <div key={p.code} className="flex items-center justify-between gap-2">
                          <span className="text-sm text-slate-600">{p.name}</span>
                          <div className="w-28">
                            <NumberInput value={rateOverride(p.code) === '' ? null : rateOverride(p.code)}
                              suffix="%" placeholder={String(p.default_rate)}
                              onChange={(n) => setRateOverride(p.code, n)}
                              data-testid={`wizard-rate-override-${p.code}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>Review</div>
              {preview ? <QualificationResults result={preview} /> : (
                <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Calculating…</div>
              )}
              {contactId ? (
                <div className="pt-3 border-t border-slate-100 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Scenario name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="wizard-scenario-name" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => saveScenario()} disabled={saving} className="btn-gold" data-testid="wizard-save-scenario">
                      {saving ? <Loader2 className="animate-spin" size={14} /> : null} {editing ? 'Save changes' : 'Save scenario'}
                    </button>
                    {editing && (
                      <button type="button" onClick={() => saveScenario({ asNew: true })} disabled={saving} className="btn-outline" data-testid="wizard-save-as-new">
                        Save as new scenario
                      </button>
                    )}
                    <button type="button" onClick={generateProgramSet} disabled={saving} className="btn-outline" data-testid="wizard-generate-program-set">
                      Generate program set
                    </button>
                  </div>
                </div>
              ) : standalone ? (
                <div className="pt-3 border-t border-slate-100 space-y-3 print:hidden" data-testid="wizard-save-to-contact">
                  <ContactPicker label="Save to contact" value={pickedContact} onChange={(id) => { if (!saving) { setPickedContact(id); setSavedTo(null); } }}
                    allowNone={false} testId="wizard-contact-picker" />
                  <div>
                    <Label className="text-xs text-slate-500">Scenario name</Label>
                    <Input value={name} onChange={(e) => { setName(e.target.value); setSavedTo(null); }} className="mt-1" data-testid="wizard-scenario-name" />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={() => saveScenario()} disabled={saving || !pickedContact || !!savedTo} className="btn-gold disabled:opacity-50" data-testid="wizard-save-scenario">
                      {saving ? <Loader2 className="animate-spin" size={14} /> : null} Save to contact
                    </button>
                    {savedTo && (
                      <Link to={`/portal/contacts/${savedTo.contact_id}?tab=qualification`} className="text-sm underline" style={{ color: '#070B5F' }} data-testid="wizard-saved-link">
                        Saved. Open the contact's Qualification tab
                      </Link>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400 pt-3 border-t border-slate-100">
                  Open a contact's Qualification tab to save scenarios.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between print:hidden">
          <button type="button" onClick={back} disabled={step === 1}
            className="btn-outline disabled:opacity-40" data-testid="wizard-back">Back</button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-800" data-testid="wizard-cancel">Cancel</button>
          )}
          {step < 5 && (
            <button type="button" onClick={next} className="btn-gold" data-testid="wizard-next">Next</button>
          )}
        </div>
      </div>

      <WizardTaxLookupDialog open={taxOpen} onOpenChange={setTaxOpen} purchasePrice={input.purchase_price} onApply={applyTaxLookup} />

      {step >= 2 && (
        <div className={`lg:col-span-1${step === 5 ? ' print:hidden' : ''}`}>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sticky top-4" data-testid="wizard-preview">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Live Preview</div>
            {calculating && !preview && (
              <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Calculating…</div>
            )}
            {calculating && preview && (
              <div className="text-xs text-slate-400 mb-2 flex items-center gap-1"><Loader2 className="animate-spin" size={11} /> Calculating…</div>
            )}
            {preview && <QualificationResults result={preview} compact />}
          </div>
        </div>
      )}
    </div>
  );
}
